import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { ColorsConstants, grayFontColor } from "../../cssUtils/constants.js";
import typography from "../../cssUtils/typography.js";
import {
  getCurrencySymbol,
  getPlanPrice,
  BILLING_CYCLE_CONFIG,
  normalizeCycle,
} from "./utils.js";

const AddonQuantityModal = ({
  open,
  onClose,
  onConfirm,
  addon,
  selectedBillingCycle,
  billingCurrency,
  unitLabel = "unit",
  currentQuantity = 0,  // > 0 means increasing an existing addon
}) => {
  const minQuantity = currentQuantity > 0 ? currentQuantity + 1 : 1;
  const [quantity, setQuantity] = useState(minQuantity);

  // Reset to sensible minimum each time the modal opens
  useEffect(() => {
    if (open) setQuantity(currentQuantity > 0 ? currentQuantity + 1 : 1);
  }, [open, currentQuantity]);

  if (!addon) return null;

  const basePrice = getPlanPrice(addon, selectedBillingCycle);
  const totalPrice = basePrice * quantity;
  const cycleSuffix =
    BILLING_CYCLE_CONFIG[normalizeCycle(selectedBillingCycle)]?.suffix ?? "";

  const formatPrice = (price) => {
    if (price === 0 || price === "0") return "Contact Support";
    return `${getCurrencySymbol(billingCurrency)}${parseFloat(price).toLocaleString("en-IN")}`;
  };

  const getDisplayName = (name) => {
    if (!name) return "";
    return name
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          ...typography.h1,
          fontWeight: "bold",
          color: ColorsConstants.primaryBackgroundColor,
          pb: 1,
        }}
      >
        {currentQuantity > 0
          ? `Increase ${getDisplayName(addon.name)}`
          : `Configure ${getDisplayName(addon.name)}`}
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {currentQuantity > 0 && (
          <Typography
            variant="body2"
            sx={{ mb: 2, color: grayFontColor }}
          >
            Currently active: <strong>{currentQuantity} {unitLabel}{currentQuantity !== 1 ? "s" : ""}</strong>.
            Enter the total quantity you want after this purchase.
          </Typography>
        )}
        <TextField
          label={currentQuantity > 0 ? `Total ${unitLabel}s after purchase` : `Quantity (${unitLabel}s)`}
          type="number"
          value={quantity}
          onChange={(e) => {
            const val = Math.max(minQuantity, parseInt(e.target.value, 10) || minQuantity);
            setQuantity(val);
          }}
          inputProps={{ min: minQuantity }}
          fullWidth
          sx={{ mb: 3, mt: currentQuantity > 0 ? 0 : 2 }}
        />

        <Box
          sx={{
            backgroundColor: ColorsConstants.primaryBlue60,
            border: `1px solid ${ColorsConstants.primaryPurple100}`,
            borderRadius: 1,
            p: 2,
          }}
        >
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
          >
            <Typography variant="body2" sx={{ color: grayFontColor }}>
              Price per {unitLabel}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: ColorsConstants.primaryBackgroundColor,
              }}
            >
              {formatPrice(basePrice)}
              {cycleSuffix}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
                color: ColorsConstants.primaryBackgroundColor,
              }}
            >
              {currentQuantity > 0
                ? `Adding ${quantity - currentQuantity} more (total: ${quantity})`
                : `Total (${quantity} ${unitLabel}${quantity !== 1 ? "s" : ""})`}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
                color: ColorsConstants.primaryPurple100,
              }}
            >
              {formatPrice(totalPrice)}
              {cycleSuffix}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: ColorsConstants.primaryGray30,
            color: grayFontColor,
            "&:hover": { borderColor: ColorsConstants.primaryGray50 },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(quantity)}
          variant="contained"
          sx={{
            backgroundColor: ColorsConstants.primaryPurple100,
            "&:hover": {
              backgroundColor: ColorsConstants.primaryBackgroundColor,
            },
          }}
        >
          Add to Plan
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddonQuantityModal;
